// imports
import tariffPackages from "@/data/tariffPackages";
import {conjugationMonth, conjugationNumber} from "@/helpers/conjugation";

const {tariffs} = tariffPackages;

export const MODES = {
    PAYMENT: "payment",
    CALCULATOR: "calculator",
}

// helpers
const buildTariffGroupsFromCounts = ({viber = 0, telegram = 0, whatsapp = 0}) => {
    const result = {
        "whatsapp+viber+telegram": 0,
        "whatsapp+viber": 0,
        "whatsapp+telegram": 0,
        "viber+telegram": 0,
        whatsapp: 0,
        viber: 0,
        telegram: 0,
    };

    // Triple combinations
    while (viber > 0 && telegram > 0 && whatsapp > 0) {
        result["whatsapp+viber+telegram"]++;
        viber--;
        telegram--;
        whatsapp--;
    }

    // Double combinations
    while (viber > 0 && whatsapp > 0) {
        result["whatsapp+viber"]++;
        viber--;
        whatsapp--;
    }

    while (telegram > 0 && whatsapp > 0) {
        result["whatsapp+telegram"]++;
        telegram--;
        whatsapp--;
    }

    while (viber > 0 && telegram > 0) {
        result["viber+telegram"]++;
        viber--;
        telegram--;
    }

    // Remaining single numbers
    result.viber = viber;
    result.telegram = telegram;
    result.whatsapp = whatsapp;

    return result;
};


const unregisterNumbers = (num) => {
    if (!Object.values(tariffs)[0][`number${num}`] && num > 1) {
        return unregisterNumbers(num - 1);
    } else if (num === 0) "number1";
    return `number${num}`;
};

const getPriceByMonthCount = (groups, vbCount, tgCount, waCount, months) => {
    const getSplitUnitPrice = (groupKey, messenger) => {
        const cnt = groups[groupKey];
        if (!cnt) return 0;
        const comboUnitPrice = getPriceOfMonth(groupKey, cnt, months);
        const parts = groupKey.split("+");
        const sumSingles = parts.reduce(
            (sum, m) => sum + getPriceOfMonth(m, 1, months),
            0
        );
        const coef = getPriceOfMonth(messenger, 1, months) / sumSingles;
        return comboUnitPrice * coef;
    };

    // Calculate total contributed cost per messenger
    const contrib = {
        viber:
            groups.viber * getPriceOfMonth("viber", groups.viber, months) +
            groups["viber+telegram"] * getSplitUnitPrice("viber+telegram", "viber") +
            groups["whatsapp+viber"] * getSplitUnitPrice("whatsapp+viber", "viber") +
            groups["whatsapp+viber+telegram"] *
            getSplitUnitPrice("whatsapp+viber+telegram", "viber"),
        telegram:
            groups.telegram * getPriceOfMonth("telegram", groups.telegram, months) +
            groups["viber+telegram"] * getSplitUnitPrice("viber+telegram", "telegram") +
            groups["whatsapp+telegram"] *
            getSplitUnitPrice("whatsapp+telegram", "telegram") +
            groups["whatsapp+viber+telegram"] *
            getSplitUnitPrice("whatsapp+viber+telegram", "telegram"),
        whatsapp:
            groups.whatsapp * getPriceOfMonth("whatsapp", groups.whatsapp, months) +
            groups["whatsapp+viber"] * getSplitUnitPrice("whatsapp+viber", "whatsapp") +
            groups["whatsapp+telegram"] *
            getSplitUnitPrice("whatsapp+telegram", "whatsapp") +
            groups["whatsapp+viber+telegram"] *
            getSplitUnitPrice("whatsapp+viber+telegram", "whatsapp"),
    };

    return  {
        viber: vbCount
            ? (contrib.viber / vbCount).toFixed(2)
            : "0.00",
        telegram: tgCount
            ? (contrib.telegram / tgCount).toFixed(2)
            : "0.00",
        whatsapp: waCount
            ? (contrib.whatsapp / waCount).toFixed(2)
            : "0.00",
    };
}

// get
const getPriceOfMonth = (messenger, numbers = 1, months = 1) => {
    const numbersInTariff = unregisterNumbers(numbers);
    return tariffs[messenger][numbersInTariff]?.[`month${months}`] || 0;
};

const getTotalPrice = (groups, months) => {
    let total = 0;

    for (const [key, count] of Object.entries(groups)) {
        if (count > 0) {
            total += getPriceOfMonth(key, count, months) * count * months;
        }
    }

    return total;
}

const getStandardPrice = (vC, tC, wC, months) => {
    const price = vC * getPriceOfMonth("viber", 1, 1) +
        tC * getPriceOfMonth("telegram", 1, 1) +
        wC * getPriceOfMonth("whatsapp", 1, 1)
    return (price * months).toFixed(2);
}

const getDiscountData = (standardPrice, totalCost) => {
    const percent = standardPrice > 0
        ? (100 - (totalCost / standardPrice) * 100)
        : 0;

    return {
        discountPrice: Math.max(0, standardPrice - totalCost).toFixed(2),
        discount: Math.round(percent)
    };
};

const getMessengerCounts = (data, mode) => {
    if (mode === MODES.CALCULATOR) {
        return {
            vbCount: data.count.viber || 0,
            tgCount: data.count.telegram || 0,
            waCount: data.count.whatsapp || 0,
        };
    } else {
        return {
            vbCount: data.viber?.length || 0,
            tgCount: data.telegram?.length || 0,
            waCount: data.whatsapp?.length || 0,
        };
    }
};

// calculator
// TODO: “t” is passed until all components that will use this service migrate to the Composition API
export const tariffCalculator = (data, mode = MODES.PAYMENT, t) => {
    const {vbCount, tgCount, waCount} = getMessengerCounts(data, mode);
    const groups = buildTariffGroupsFromCounts({viber: vbCount, telegram: tgCount, whatsapp: waCount});
    const months = data.count.months;

    const totalCost = getTotalPrice(groups, months);
    const standardPrice = getStandardPrice(vbCount, tgCount, waCount, months);

    const priceByMonthCount = getPriceByMonthCount(groups, vbCount, tgCount, waCount, months);
    return {
        summary: {
            standardPrice,
            monthCount: conjugationMonth(months, t),
            numberCountByMsg: {
                viber: conjugationNumber(vbCount, t),
                telegram: conjugationNumber(tgCount, t),
                whatsapp: conjugationNumber(waCount, t),
            },
        },
        benefit: getDiscountData(standardPrice, totalCost),
        priceByMonthCount,
        totalCost: totalCost.toFixed(2),
    };
};

// TODO: need to add new combination with whatsapp

